// @flow
import { alerts, devices, feedback } from '~/core/constants';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PRODUCT_MODE_CHANGE, PRODUCT_MODE_CHANGE_UPDATE_PRODUCT, GET_PRODUCTS } from './constants';
import { productModeChangeUpdateProduct, receiveProducts, undoProductChange } from './actions';
import { products as api } from '~/core/api';
import { buildKey } from '~/core/utils/app-settings';
import { deviceTypes, capabilities } from '~/core/constants/devices';
import { getAppSetting } from '~/core/state/app-settings/selectors';
import { getTypeForProduct } from './selectors';
import { getUserId } from '~/core/state/user/selectors';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import type { Effect } from 'redux-saga';
import { MANUAL_TARGET_SETTING } from '~/core/constants/app-settings';
import { noop } from 'lodash/fp';
import { patchProduct } from '~/core/api/products/products.api';
import { requestAlert } from '~/core/state/alerts/actions';
import { SAVE_GROUP_COMPLETE } from '../groups/constants';
import { getConfig } from '~/core/state/config/selectors';
import { filterOutDeviceGroups } from '~/core/state/products/utils';
import { getHubHasCapability } from '~/core/state/devices/selectors';

export function* getProductsSaga(): Generator<noop | Effect, *, *> {
    const [products, error] = yield call(handleApi(api.getProducts));
    const deviceGroupsEnabled = yield select(getConfig, 'features:deviceGroups');
    const hubSupportsGroups = yield select(getHubHasCapability, capabilities.PRODUCT_GROUP);

    const filteredProducts = products
        ? deviceGroupsEnabled && hubSupportsGroups ? products : filterOutDeviceGroups(products)
        : undefined;

    yield put(receiveProducts(error || { products: filteredProducts, loadedAt: new Date() }));

    if (error) {
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }
}

function* watchGetProductsSaga(): Generator<Effect, *, *> {
    yield takeLatest(GET_PRODUCTS, handleSaga(getProductsSaga));
}

function* watchSaveGroupCompleteSaga(): Generator<Effect, *, *> {
    yield takeLatest(SAVE_GROUP_COMPLETE, handleSaga(getProductsSaga));
}

export function* changeProductModeSaga(action: Object): Generator<Effect | noop, *, *> {
    const { payload } = action;
    const { productId, type, mode, currentSchedule, defaultTargetTemperature } = payload;
    const props = { id: productId };
    let cookieTarget;

    const productType = yield select(getTypeForProduct, props);

    const update: Object = { mode: mode };
    if (productType === deviceTypes.HEATING && mode === devices.deviceModes.MANUAL) {
        cookieTarget = yield select(getAppSetting, buildKey(MANUAL_TARGET_SETTING, productId));
        update.target = cookieTarget ? parseFloat(cookieTarget) : defaultTargetTemperature;
    }

    const userId = yield select(getUserId);
    yield put(
        productModeChangeUpdateProduct({
            userId,
            mode,
            type,
            productId,
            currentSchedule,
            defaultTargetTemperature,
            cookieTarget
        })
    );

    const [, error] = yield call(handleApi(patchProduct), update, productType, productId);

    if (error) {
        yield put(undoProductChange(PRODUCT_MODE_CHANGE_UPDATE_PRODUCT));
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }
}

function* watchChangeProductModeSaga(): Generator<Effect, *, *> {
    yield takeLatest(PRODUCT_MODE_CHANGE, handleSaga(changeProductModeSaga));
}

export default [watchChangeProductModeSaga, watchGetProductsSaga, watchSaveGroupCompleteSaga];



// WEBPACK FOOTER //
// ./src/core/state/products/sagas.js